<template>
  <div class="tracking-frame">
    <!-- 加载中（含 Session 可重试退避中 / iframe 加载中）：覆盖在 iframe 之上，避免白屏 -->
    <div v-show="loadStatus === 'loading'" class="tracking-state tracking-loading tracking-overlay">
      <div class="state-spinner">
        <span class="spinner-ring"></span>
        <i class="el-icon-loading spinner-icon"></i>
      </div>
      <h3 class="state-title">物流宝加载中</h3>
      <p class="state-desc">{{ retryHint || '正在为您接入物流宝服务，请稍候…' }}</p>
    </div>

    <!-- 不可用的错误态：保留页面，展示友好提示而非白屏 -->
    <div v-if="loadStatus === 'error'" class="tracking-state tracking-error">
      <div class="state-badge">
        <i class="el-icon-warning-outline"></i>
      </div>
      <h3 class="state-title">物流宝暂不可用</h3>
      <p class="state-desc">{{ errorMessage || '服务暂时无法连接，请稍后重试。' }}</p>
    </div>

    <!-- 正常加载 iframe：常驻 DOM，加载完成前由 loading 遮罩覆盖，避免白屏 -->
    <div v-show="loadStatus === 'ready'">
      <div class="notice-wlb">
        <span class="notice-icon"
          ><svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="20" height="20" class="icon">
            <path
              d="M622.592 81.92c-13.312 0-25.6 4.096-36.864 11.264l-348.16 230.4c-14.336 9.216-31.744 15.36-49.152 15.36h-69.632C80.896 337.92 51.2 367.616 51.2 404.48v202.752c0 36.864 29.696 66.56 66.56 66.56h69.632c17.408 0 34.816 5.12 49.152 15.36l349.184 230.4c11.264 7.168 23.552 11.264 36.864 11.264 36.864 0 66.56-29.696 66.56-66.56V148.48c1.024-36.864-29.696-66.56-66.56-66.56zM102.4 607.232V404.48c0-8.192 7.168-15.36 15.36-15.36H184.32v233.472h-66.56c-8.192 0-15.36-7.168-15.36-15.36z m535.552 256c0 9.216-8.192 15.36-15.36 15.36-3.072 0-6.144-1.024-8.192-3.072L265.216 646.144c-8.192-5.12-16.384-10.24-25.6-13.312V378.88c9.216-3.072 17.408-8.192 25.6-13.312L614.4 135.168c3.072-2.048 5.12-3.072 8.192-3.072 7.168 0 15.36 6.144 15.36 15.36v715.776zM797.696 386.048c-11.264-9.216-27.648-7.168-35.84 4.096-9.216 11.264-7.168 27.648 4.096 35.84 27.648 22.528 44.032 56.32 44.032 92.16 0 34.816-15.36 67.584-41.984 90.112-11.264 9.216-12.288 25.6-3.072 35.84 5.12 6.144 12.288 9.216 19.456 9.216 6.144 0 11.264-2.048 16.384-6.144 38.912-31.744 60.416-79.872 60.416-130.048 0-50.176-22.528-98.304-63.488-131.072z"
              fill="#1170EB"
              p-id="2446"
            ></path>
            <path
              d="M867.328 271.36c-11.264-8.192-27.648-5.12-35.84 7.168-8.192 11.264-5.12 27.648 7.168 35.84 66.56 45.056 106.496 119.808 106.496 199.68 0 84.992-43.008 161.792-115.712 205.824-12.288 7.168-16.384 23.552-8.192 34.816 5.12 8.192 13.312 12.288 21.504 12.288 4.096 0 9.216-1.024 13.312-4.096 88.064-53.248 140.288-146.432 140.288-249.856-1.024-96.256-49.152-186.368-129.024-241.664z"
              fill="#1170EB"
              p-id="2447"
            ></path>
          </svg>
        </span>
        <span>e航无忧与物流宝达成战略合作！如需了解更多物流宝服务，请联系：15900729336</span>
      </div>
      <iframe ref="wlbIframe" class="tracking-iframe" title="tracking" :src="iframeSrc" @load="onIframeLoad" />
    </div>
  </div>
</template>

<script>
import { CPSHOST } from '@/utils/appContent'
import actions from '@/actions'

// 物流宝页面相关配置，按需修改
// WebSocket 换签通知地址（与文档及后端一致）。注意必须用「同一站点登录令牌」建立连接
const WLB_WS_URL = `ws://${CPSHOST}/ws/wlb/token-refresh`
const WLB_SESSION_API = `${location.hostname.indexOf('eline56.com') > -1 ? `https://${CPSHOST}/wlb/session` : `http://${CPSHOST}/wlb/session`}` // 物流宝会话接口（同一站点登录令牌鉴权）
const WS_RECONNECT_DELAY = 3000 // 断线重连基础间隔(ms)

export default {
  name: 'Tracking',
  data() {
    return {
      iframeSrc: null,
      pendingToken: null, // iframe 就绪前缓存的待处理换签 token
      loadStatus: 'loading', // loading | ready | error
      iframeLoaded: false, // iframe 是否已完成加载
      retryHint: '', // 重试中的提示文案
      ws: null,
      reconnectTimer: null,
      loadTimer: null,
      retryCount: 0,
      errorMessage: ''
    }
  },
  mounted() {
    // 首次加载：取同一站点登录令牌、建立 WS、请求 Session 接口加载 iframe
    this.loadSession()
  },
  beforeDestroy() {
    this.closeWebSocket()
    if (this.loadTimer) {
      clearTimeout(this.loadTimer)
      this.loadTimer = null
    }
  },
  methods: {
    // ============ Session 接口 ============
    // 取 iframeUrl 加载 iframe
    async loadSession() {
      try {
        const response = await fetch(WLB_SESSION_API, {
          method: 'GET',
          headers: {
            token: localStorage.getItem('userToken') // 使用同一站点登录令牌
          }
        })
        const result = await response.json()
        if (result.code == 401) {
          actions.setGlobalState({ isLogin: false })
          this.$router.push({ path: '/user/register' })
          return
        }
        if (!result.success) {
          this.retryHint = result.message
        }

        // 响应结构：{ success, retryable, data: { accessToken, expiresAt, iframeUrl } }
        if (result.success && result.data && result.data.iframeUrl) {
          // 拿到地址：先进入「iframe 加载中」，等 iframe 真正 load 完成再切 ready，避免白屏
          this.retryHint = '正在加载物流宝页面，请稍候…'
          this.iframeLoaded = false
          this.loadStatus = 'loading'
          this.iframeSrc = result.data.iframeUrl
          return
        }

        // 物流宝会话正在创建, 继续重试（退避中）
        if (result.code == 'WLB_EXCHANGE_IN_PROGRESS') {
          this.loadStatus = 'loading'
          this.retryHint = result.message || '物流宝会话创建中，正在重试（退避中）…'
          this.scheduleRetry()
        }
        // 物流宝服务暂不可用, 停止自动重试并展示
        else if (result.code == 'WLB_UPSTREAM_UNAVAILABLE') {
          this.loadStatus = 'error'
          this.errorMessage = result.message
        }
      } catch (e) {
        // 网络异常按可重试处理
        this.loadStatus = 'loading'
        this.retryHint = '网络连接异常，正在重试（退避中）…'
      }
    },

    // iframe 加载完成：隐藏 loading 遮罩，显示页面
    onIframeLoad() {
      this.iframeLoaded = true
      this.retryHint = ''
      this.loadStatus = 'ready'
    },

    // Session 创建中的退避重试：指数退避（3s -> 6s -> 12s ...），上限约 30s
    scheduleRetry() {
      if (this.loadTimer) clearTimeout(this.loadTimer)
      this.retryCount = (this.retryCount || 0) + 1
      const delay = Math.min(WS_RECONNECT_DELAY * 2 ** (this.retryCount - 1), 30000)
      this.loadTimer = setTimeout(() => {
        this.loadSession()
      }, delay)
    },

    // ============ WebSocket 换签通知 ============
    // 使用同一站点登录令牌建立连接：/ws/wlb/token-refresh?token=<站点令牌>
    connectWebSocket() {
      this.closeWebSocket()

      const wsUrl = `${WLB_WS_URL}?token=${localStorage.getItem('userToken')}`
      const socket = new WebSocket(wsUrl)
      this.ws = socket

      socket.onopen = () => {
        // 连接成功：重连计数重置；服务端会补发当前有效 token（首次连接无需自行请求）
        console.log('[Tracking] 物流宝 WebSocket 已连接')
      }

      socket.onmessage = event => {
        try {
          const data = JSON.parse(event.data)
          // 收到新 token，通知 iframe
          if (data.type === 'TOKEN_REFRESH') {
            const newToken = localStorage.getItem('userToken')
            const iframe = this.$refs.wlbIframe
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage({ type: 'TOKEN_REFRESH', token: newToken }, '*')
            }
          }
        } catch (e) {
          console.error('[Tracking] 解析 WebSocket 消息失败:', e)
        }
      }

      socket.onclose = () => {
        // 按前端统一重连策略重连（重连成功时服务端会补发当前有效 token）
        this.reconnectTimer = setTimeout(() => this.connectWebSocket(), WS_RECONNECT_DELAY)
      }

      socket.onerror = err => {
        console.info('[Tracking] WebSocket 发生错误:', err)
        // 出错后关闭以触发重连
        socket.close()
      }
    },

    // 关闭 WebSocket 连接与重连定时器
    closeWebSocket() {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
      if (this.ws) {
        this.ws.onclose = null // 避免触发重连
        this.ws.close()
        this.ws = null
      }
    }
  }
}
</script>

<style scoped lang="scss">
.tracking-frame {
  position: relative;
  width: 100%;
  /* 占满 header(60px) 与 footer 之间的剩余视口高度 */
  height: calc(100vh - 100px);
  overflow: hidden;
}

.tracking-iframe {
  display: block;
  width: 100%;
  height: calc(100vh - 140px);
  border: 0;
}

/* 加载中 / 错误态：居中友好提示，避免白屏 */
.tracking-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  text-align: center;
  background: #fff;
  box-sizing: border-box;
}

/* 加载遮罩：覆盖在 iframe 之上，iframe 加载完成前保持显示，避免白屏 */
.tracking-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
}

/* 标题与描述 */
.state-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1d2733;
  letter-spacing: 0.5px;
}

.state-desc {
  margin: 12px 0 0;
  max-width: 360px;
  font-size: 14px;
  line-height: 1.7;
  color: #7a8493;
}

/* 加载态：脉冲圆环 + 居中图标 */
.tracking-loading .state-spinner {
  position: relative;
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.tracking-loading .spinner-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid rgba(29, 115, 255, 0.15);
  border-top-color: #1d73ff;
  animation: tracking-spin 0.9s linear infinite;
}

.tracking-loading .spinner-icon {
  font-size: 26px;
  color: #1d73ff;
  animation: tracking-pulse 1.4s ease-in-out infinite;
}

@keyframes tracking-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes tracking-pulse {
  0%,
  100% {
    opacity: 0.55;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

/* 错误态：渐变徽章 + 主按钮 */
.tracking-error .state-badge {
  width: 76px;
  height: 76px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #fff1d6 0%, #ffe3c2 100%);
  box-shadow: 0 10px 24px rgba(255, 186, 0, 0.25);
}

.tracking-error .state-badge i {
  font-size: 38px;
  color: #ff9f1c;
}

.tracking-error .el-button {
  margin-top: 28px;
  padding: 11px 32px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 8px 18px rgba(29, 115, 255, 0.28);
}

.notice-wlb {
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  background: #e7f0fd;
  position: relative;
  -moz-user-select: none;
  user-select: none;
  -webkit-user-select: none;
  font-size: 14px;

  .notice-icon {
    width: 20px;
    height: 20px;
    padding-right: 30px;
  }
}
</style>
